import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn
} from '@repo/ui'
import {
  AlertCircle,
  Archive,
  Check,
  ClipboardList,
  Copy,
  MoreHorizontal,
  PanelRight,
  RotateCcw,
  RotateCw,
  X
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  abandonConversationQuestion,
  continueConversationQuestions,
  retryConversationChatExecution
} from '@/api/conversation'
import type { ConversationRun } from '@/api/conversation'
import { ConversationComposer } from './Composer'
import { ConversationTranscript } from './Transcript'
import { ChatInspector } from './ConversationPanel'
import { ChatContextPanel, useChatContextRoom } from './ChatContext'
import { selectChatContext } from './chatContextState'
import { stageLabel } from './eventCopy'
import { selectActiveConversationTurn, selectConversationTurnPresentations } from './state'
import type {
  ConversationComposerContextActions,
  ConversationComposerLoader,
  ConversationComposerOption,
  ConversationComposerUploadResult
} from './Composer'
import type { ReactNode } from 'react'
import type { ConversationPanelServices } from './ConversationPanel'
import type { ConversationController } from './controller'

// The two scopes the shell serves, kept apart in the type system so a caller cannot
// open a workspace artifact into the chat's surface or the other way round. A slide is
// not its own document, only a position inside a presentation, so it narrows the
// presentation canvas rather than forming a kind of its own. See ADR-167.
export type WorkspaceCanvasIdentity =
  // A proposal opens as a whole when no section is named, which is what a freshly
  // created one has: sections exist but none is drafted, so none is worth focusing.
  | { canvas: 'proposal'; proposalId: number; sectionId?: number }
  // A slide is addressed by the slide, never by a version of it. The version this used to
  // carry reached a cache key and a staleness guard and nothing that renders, and the guard
  // refused any address whose version was not the selected one: every `Show this slide` from
  // before the latest refine dead-ended. See the decision note on slide addresses.
  | { canvas: 'presentation'; presentationId: number; slideId?: number }
  // A document has revisions rather than parts, and the canvas picks which to show, so
  // the identity is the document alone.
  | { canvas: 'document'; documentId: number }
  // Content is a corpus rather than one of the workspace's artifacts: a file, a stored
  // text and a connector are rows the Content page owns, so each identity is the row and
  // has no part to focus and no revision to pick.
  // A file and a text carry the scope they were read at as well. Not because the id is
  // ambiguous: rag_docs.doc_id is one identity sequence over the whole table, and
  // project_id arrived later as a nullable column, so a workspace row and an organisation
  // row can never share an id. The scope is here because every read and write filters on
  // project_id, the organisation's rows being the ones holding none, so a row has to be
  // asked for at the scope it lives at or it is not found. An organisation file asked for
  // under this workspace's id comes back as an empty page, never as a different file.
  | { canvas: 'file'; fileId: number; scope: 'workspace' | 'global' }
  | { canvas: 'text'; docId: number; scope: 'workspace' | 'global' }
  | { canvas: 'connector'; connectorId: number }
  | { canvas: 'collection'; collection: ConversationCollectionKind }

export type ChatInspectorIdentity =
  | { inspector: 'facts'; briefId: number }
  | { inspector: 'sources'; messageId: number }
  | { inspector: 'activity'; originMessageId: number }

export type ConversationCollectionKind = 'proposals' | 'presentations' | 'content' | 'connectors'

function ConversationHeader({
  navigationControl,
  title,
  projectName,
  status,
  readOnly,
  originKind,
  cutoverReadOnly,
  showReadOnlyNotice,
  onRename,
  onArchive,
  onRestore,
  onCopyLink,
  onOpenFacts,
  contextControl
}: {
  navigationControl?: ReactNode
  title: string
  projectName: string
  status?: string
  readOnly: boolean
  originKind: 'native' | 'legacy-search' | 'legacy-marketing'
  cutoverReadOnly: boolean
  showReadOnlyNotice: boolean
  onRename?: (title: string) => Promise<void>
  onArchive?: () => Promise<void>
  onRestore?: () => Promise<void>
  onCopyLink?: () => Promise<void> | void
  onOpenFacts?: () => void
  contextControl?: ReactNode
}) {
  const originLabel =
    originKind === 'legacy-search'
      ? 'Imported from Search Agent'
      : originKind === 'legacy-marketing'
        ? 'Imported from Marketing Agent'
        : undefined
  const [displayTitle, setDisplayTitle] = useState(title)
  const [draft, setDraft] = useState(title)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const titleHeadingRef = useRef<HTMLHeadingElement>(null)
  const noticeVisible = readOnly && showReadOnlyNotice
  const [error, setError] = useState<string>()
  const titleInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editing) titleInputRef.current?.focus()
  }, [editing])
  const save = async () => {
    const next = draft.trim()
    if (next === '' || next.length > 120 || onRename === undefined) return
    setBusy(true)
    setError(undefined)
    try {
      await onRename(next)
      setDisplayTitle(next)
      setDraft(next)
      setEditing(false)
    } catch (cause) {
      setError(cause instanceof Error && cause.message !== '' ? cause.message : 'Chat title could not be changed.')
    } finally {
      setBusy(false)
    }
  }
  // Abandoning the rename restores the committed title and leaves edit mode. Shared by
  // the explicit Cancel control and by Escape, which every other text-entry surface in
  // the app honours.
  const cancelRename = () => {
    setDraft(displayTitle)
    setEditing(false)
    setError(undefined)
  }
  const run = async (operation: (() => Promise<void>) | undefined, fallback: string) => {
    if (operation === undefined || busy) return
    setBusy(true)
    setError(undefined)
    try {
      await operation()
    } catch (cause) {
      setError(cause instanceof Error && cause.message !== '' ? cause.message : fallback)
    } finally {
      setBusy(false)
    }
  }

  const restore = async () => {
    if (onRestore === undefined || busy || cutoverReadOnly) return
    setRestoring(true)
    await run(async () => {
      await onRestore()
      titleHeadingRef.current?.focus()
    }, 'Chat could not be restored.')
    setRestoring(false)
  }

  return (
    <>
      <header className="flex min-h-14 shrink-0 items-center gap-3 border-b px-4 py-2 md:pl-14">
        {navigationControl}
        <div className="min-w-0 flex-1">
          {editing ? (
            <>
              <h1 ref={titleHeadingRef} id="conversation-title" tabIndex={-1} className="sr-only">
                {displayTitle}
              </h1>
              <form
                className="flex max-w-md items-center gap-1"
                onSubmit={(event) => {
                  event.preventDefault()
                  void save()
                }}
              >
                <Input
                  ref={titleInputRef}
                  aria-label="Chat title"
                  value={draft}
                  maxLength={120}
                  disabled={busy}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    // Escape abandons the rename, as it does in the rail's rename dialog.
                    // Entering edit mode focuses this input, so handling it here covers the
                    // real case without putting a key listener on the non-interactive form.
                    if (event.key !== 'Escape' || busy) return
                    event.preventDefault()
                    event.stopPropagation()
                    cancelRename()
                  }}
                />
                <Button
                  type="submit"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Save chat title"
                  disabled={busy || draft.trim() === ''}
                >
                  <Check aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Cancel chat rename"
                  disabled={busy}
                  onClick={cancelRename}
                >
                  <X aria-hidden="true" />
                </Button>
              </form>
            </>
          ) : (
            <div className="flex min-w-0 items-center gap-1">
              {/* Focusable without being in the tab order: arriving from another surface
                (the canvas' provenance line) moves focus here, because the control that
                sent the reader unmounts on arrival and focus would otherwise fall to the
                body. */}
              <h1
                ref={titleHeadingRef}
                id="conversation-title"
                tabIndex={-1}
                aria-label={displayTitle}
                className="min-w-0 truncate text-sm font-semibold"
              >
                {onRename === undefined ? (
                  displayTitle
                ) : (
                  <button
                    type="button"
                    className="max-w-full truncate rounded-sm text-left outline-none hover:underline hover:underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Rename chat"
                    onClick={() => setEditing(true)}
                  >
                    {displayTitle}
                  </button>
                )}
              </h1>
            </div>
          )}
          <div className="flex min-w-0 items-center gap-2 text-[0.6875rem] text-muted-foreground">
            <span className="truncate">{projectName}</span>
            {status !== undefined && (
              <>
                <span aria-hidden="true">·</span>
                <span>{status}</span>
              </>
            )}
            {originLabel !== undefined && (
              <>
                <span aria-hidden="true">·</span>
                <span>{originLabel}</span>
              </>
            )}
            {cutoverReadOnly && <span>Moving history</span>}
          </div>
          {error !== undefined && !noticeVisible && (
            <p className="mt-1 text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        {/*
        The facts panel's only route used to be the Open button on a brief-change card in
        the transcript. Those cards only appear after a correction, and a correction can
        only be made inside the panel, so once the nameless `Brief updated` card was
        dropped the panel could not be reached from the chat at all. It belongs on a
        durable control rather than on whether an event happened to land.
      */}
        {onOpenFacts !== undefined && (
          <Button type="button" variant="ghost" size="icon-sm" aria-label="What I have" onClick={onOpenFacts}>
            <ClipboardList aria-hidden="true" />
          </Button>
        )}
        {contextControl}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon-sm" aria-label="Chat actions" disabled={busy}>
              <MoreHorizontal aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onCopyLink !== undefined && (
              <DropdownMenuItem onClick={() => void run(async () => onCopyLink(), 'Chat link could not be copied.')}>
                <Copy aria-hidden="true" /> Copy chat link
              </DropdownMenuItem>
            )}
            {readOnly
              ? !cutoverReadOnly &&
                onRestore !== undefined && (
                  <DropdownMenuItem onClick={() => void restore()}>
                    <RotateCcw aria-hidden="true" /> Restore chat
                  </DropdownMenuItem>
                )
              : onArchive !== undefined && (
                  <DropdownMenuItem onClick={() => void run(onArchive, 'Chat could not be archived.')}>
                    <Archive aria-hidden="true" /> Archive chat
                  </DropdownMenuItem>
                )}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      {noticeVisible && (
        <div
          role="status"
          aria-label="Chat status"
          className="flex shrink-0 flex-col gap-3 border-b border-warning/30 bg-warning/10 px-4 py-3 @sm/conversation-shell:flex-row @sm/conversation-shell:items-center @sm/conversation-shell:px-6"
        >
          <div className="flex min-w-0 flex-1 items-start gap-3">
            {cutoverReadOnly ? (
              <AlertCircle className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden="true" />
            ) : (
              <Archive className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden="true" />
            )}
            <div className="min-w-0 text-sm">
              <p className="font-semibold text-foreground">
                {cutoverReadOnly ? 'History is being moved' : 'Chat archived'}
              </p>
              <p className="text-foreground/80">
                {cutoverReadOnly
                  ? 'This imported conversation is temporarily read-only while its history is being moved.'
                  : onRestore !== undefined
                    ? 'Restore this chat to continue working.'
                    : 'This chat is read-only.'}
              </p>
              {error !== undefined && (
                <p className="mt-1 text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
            </div>
          </div>
          {!cutoverReadOnly && onRestore !== undefined && (
            <Button
              type="button"
              variant="outline"
              className="ml-8 shrink-0 self-start border-warning/40 bg-background/80 @sm/conversation-shell:ml-0 @sm/conversation-shell:self-center"
              disabled={busy}
              onClick={() => void restore()}
            >
              <RotateCcw className={cn('size-4', restoring && 'animate-spin')} aria-hidden="true" />
              {restoring ? 'Restoring…' : 'Restore chat'}
            </Button>
          )}
        </div>
      )}
    </>
  )
}

function SurfaceMessage({ title, message, action }: { title: string; message: string; action?: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-md flex-1 flex-col items-center justify-center px-6 text-center">
      <AlertCircle className="mb-4 size-6 text-muted-foreground" aria-hidden="true" />
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

/**
 * The transcript's own recovery commands. They are injectable for the same reason the
 * panel's are: the surface tests drive them without a network.
 */
export type ConversationQuestionServices = {
  abandonQuestion: typeof abandonConversationQuestion
  continueQuestions: typeof continueConversationQuestions
  retryExecution: (projectId: number, conversationId: number, executionId: number) => Promise<ConversationRun>
}

const defaultQuestionServices: ConversationQuestionServices = {
  abandonQuestion: abandonConversationQuestion,
  continueQuestions: continueConversationQuestions,
  retryExecution: retryConversationChatExecution
}

export function ConversationShell({
  navigationControl,
  projectId,
  conversationId,
  chatTitle,
  projectName,
  currentUserId,
  globalContentEnabled,
  attachmentsEnabled,
  controller,
  readOnly = false,
  originKind = 'native',
  cutoverReadOnly = false,
  collapsed = false,
  allowFloatingContext = true,
  inspectorIdentity,
  onOpenCanvas = () => undefined,
  onOpenInspector = () => undefined,
  onCloseInspector = () => undefined,
  onRenameChat,
  onArchiveChat,
  onRestoreChat,
  onCopyLink,
  panelServices,
  questionServices = defaultQuestionServices,
  loadTags = () => Promise.resolve([]),
  loadDocuments = () => Promise.resolve([]),
  createTag,
  uploadDocument,
  suggestions,
  children
}: {
  navigationControl?: ReactNode
  projectId?: number
  conversationId?: number
  chatTitle?: string
  projectName: string
  currentUserId?: number
  globalContentEnabled?: boolean
  attachmentsEnabled?: boolean
  controller: ConversationController
  readOnly?: boolean
  originKind?: 'native' | 'legacy-search' | 'legacy-marketing'
  cutoverReadOnly?: boolean
  collapsed?: boolean
  allowFloatingContext?: boolean
  inspectorIdentity?: ChatInspectorIdentity
  onOpenCanvas?: (identity: WorkspaceCanvasIdentity) => void
  onOpenInspector?: (identity: ChatInspectorIdentity) => void
  onCloseInspector?: () => void
  onRenameChat?: (title: string) => Promise<void>
  onArchiveChat?: () => Promise<void>
  onRestoreChat?: () => Promise<void>
  onCopyLink?: () => Promise<void> | void
  panelServices?: ConversationPanelServices
  questionServices?: ConversationQuestionServices
  loadTags?: ConversationComposerLoader
  loadDocuments?: ConversationComposerLoader
  createTag?: (name: string) => Promise<ConversationComposerOption>
  uploadDocument?: (file: File) => Promise<ConversationComposerUploadResult>
  suggestions?: ReadonlyArray<string>
  children?: ReactNode
}) {
  const panelInvoker = useRef<HTMLElement | null>(null)
  const contextTrigger = useRef<HTMLButtonElement>(null)
  const contextAction = useRef<(() => void) | undefined>(undefined)
  const [contextCollapsed, setContextCollapsed] = useState(false)
  const [contextSheetOpen, setContextSheetOpen] = useState(false)
  const [selectedSourceMessage, setSelectedSourceMessage] = useState<number>()
  const [pendingDocuments, setPendingDocuments] = useState<Array<{ id: number; label: string }>>([])
  const composerContext = useRef<ConversationComposerContextActions>(null)
  const { ref: contextContainer, hasRoom } = useChatContextRoom()
  const selection = selectChatContext(
    controller.state,
    inspectorIdentity?.inspector === 'sources' ? inspectorIdentity.messageId : selectedSourceMessage
  )
  const hasContext = selection.sourceIds.length > 0 || selection.documents.length > 0 || pendingDocuments.length > 0
  const contextAvailable =
    controller.status === 'ready' && children === undefined && (hasContext || (attachmentsEnabled && !readOnly))
  const canFloatContext = hasRoom && allowFloatingContext && inspectorIdentity === undefined
  const floatContext = contextAvailable && canFloatContext && !contextCollapsed && !contextSheetOpen
  const fromContext = (action: () => void) => {
    if (contextSheetOpen) {
      contextAction.current = action
      setContextSheetOpen(false)
    } else action()
  }
  // The composer and the inspector are siblings here, so the shell owns the channel
  // between them. The nonce rises on every request so pressing the same open slot's
  // arrow twice seeds twice rather than looking broken the second time.
  const [composerSeed, setComposerSeed] = useState<{ text: string; nonce: number }>()
  const seedComposer = (text: string) => setComposerSeed((current) => ({ text, nonce: (current?.nonce ?? 0) + 1 }))
  const turns = useMemo(() => selectConversationTurnPresentations(controller.state), [controller.state])
  const activeTurn = selectActiveConversationTurn(turns)
  const headerStatus =
    controller.status === 'ready'
      ? readOnly
        ? cutoverReadOnly
          ? 'Read-only'
          : 'Archived'
        : controller.error !== undefined
          ? 'Disconnected'
          : activeTurn?.phase === 'waiting'
            ? 'Needs your answer'
            : activeTurn?.phase === 'writing'
              ? 'Writing'
              : activeTurn?.phase === 'working' || activeTurn?.phase === 'submitted'
                ? activeTurn.activeSummary || stageLabel(activeTurn.activeStage) || 'Working'
                : activeTurn?.phase === 'stopped'
                  ? 'Work stopped'
                  : undefined
      : controller.status === 'failure'
        ? 'Failed'
        : controller.status === 'loading'
          ? 'Loading'
          : controller.status === 'unavailable'
            ? 'Unavailable'
            : 'Refresh required'
  const rememberInvoker = () => {
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement.closest('[data-testid="chat-inspector-slot"]') === null
    ) {
      panelInvoker.current = document.activeElement
    }
  }
  const openInspector = (identity: ChatInspectorIdentity) => {
    if (identity.inspector === 'sources') setSelectedSourceMessage(identity.messageId)
    rememberInvoker()
    onOpenInspector(identity)
  }
  // No invoker bookkeeping here: the canvas belongs to the workspace, which owns
  // restoring focus to whatever opened it.
  const openCanvas = onOpenCanvas
  // The newest brief is the one the chat is working on, and the only one the header can
  // sensibly offer. A chat with no brief yet has nothing to show, so it offers nothing.
  const currentBriefId = controller.state.briefs.at(-1)?.briefId
  const pendingQuestion = controller.state.questions.at(-1)
  const pendingQuestionKey =
    pendingQuestion === undefined
      ? undefined
      : `${pendingQuestion.runId}:${pendingQuestion.suspensionKey}:${pendingQuestion.slotKey}`
  const contextPanel = (
    <ChatContextPanel
      selection={selection}
      pendingDocuments={readOnly ? [] : pendingDocuments}
      {...(panelServices === undefined ? {} : { loadSourceSummaries: panelServices.getSourceSummaries })}
      onOpenSources={(messageId) => fromContext(() => openInspector({ inspector: 'sources', messageId }))}
      onOpenFacts={(briefId) => fromContext(() => openInspector({ inspector: 'facts', briefId }))}
      onLatest={() => {
        setSelectedSourceMessage(undefined)
        if (inspectorIdentity?.inspector === 'sources') onCloseInspector()
      }}
      {...(attachmentsEnabled && !readOnly
        ? {
            onAddSources: () => fromContext(() => composerContext.current?.openDocuments()),
            ...(uploadDocument === undefined
              ? {}
              : {
                  onUploadSource: () => {
                    // Keep the native chooser in the user's click, before closing a sheet.
                    composerContext.current?.uploadFile()
                    setContextSheetOpen(false)
                  }
                })
          }
        : {})}
    />
  )
  return (
    <div
      // Hidden rather than unmounted when the canvas is widened: unmounting would tear
      // down the live conversation stream and reopen it on the way back.
      className={cn('flex min-h-0 flex-1 overflow-hidden bg-background', collapsed && 'hidden')}
    >
      <section
        ref={contextContainer}
        className="@container/conversation-shell flex min-h-0 min-w-0 flex-1 flex-col"
        aria-labelledby="conversation-title"
      >
        <ConversationHeader
          key={`${conversationId ?? controller.state.threadId ?? 'unscoped'}:${chatTitle ?? projectName}`}
          title={chatTitle ?? `${projectName} conversation`}
          {...(navigationControl === undefined ? {} : { navigationControl })}
          projectName={projectName}
          {...(headerStatus === undefined ? {} : { status: headerStatus })}
          readOnly={readOnly}
          originKind={originKind}
          cutoverReadOnly={cutoverReadOnly}
          showReadOnlyNotice={controller.status === 'ready' && children === undefined}
          contextControl={
            contextAvailable && (
              <Button
                ref={contextTrigger}
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Chat context"
                aria-expanded={floatContext || contextSheetOpen}
                onClick={() => {
                  if (floatContext) setContextCollapsed(true)
                  else if (canFloatContext) {
                    setContextCollapsed(false)
                    setContextSheetOpen(false)
                  } else setContextSheetOpen(true)
                }}
              >
                <PanelRight className="size-4" aria-hidden="true" />
                <span className="hidden sm:inline">Context</span>
              </Button>
            )
          }
          {...(onRenameChat === undefined ? {} : { onRename: onRenameChat })}
          {...(onArchiveChat === undefined ? {} : { onArchive: onArchiveChat })}
          {...(onRestoreChat === undefined ? {} : { onRestore: onRestoreChat })}
          {...(onCopyLink === undefined ? {} : { onCopyLink })}
          {...(currentBriefId === undefined
            ? {}
            : { onOpenFacts: () => openInspector({ inspector: 'facts', briefId: currentBriefId }) })}
        />
        {controller.status === 'ready' && children === undefined && controller.error && (
          <div className="shrink-0 border-b bg-muted/40 px-4 py-2 text-center text-xs text-muted-foreground">
            Live updates paused.
            <Button
              type="button"
              variant="link"
              size="sm"
              className="ml-1 h-auto p-0 text-xs"
              onClick={controller.reconnect}
            >
              Reconnect
            </Button>
          </div>
        )}
        {controller.status === 'loading' && (
          <div className="flex flex-1 items-center justify-center" role="status">
            <span className="text-sm text-muted-foreground">Loading conversation</span>
          </div>
        )}
        {controller.status === 'unavailable' && (
          <SurfaceMessage
            title="Conversation is not available"
            message={controller.error ?? 'This workspace does not currently have a conversation capability.'}
          />
        )}
        {controller.status === 'unsupported' && (
          <SurfaceMessage
            title="Refresh required"
            message="This conversation uses a newer protocol. Reload Snowvault to continue."
          />
        )}
        {controller.status === 'failure' && (
          <SurfaceMessage
            title="Conversation could not be loaded"
            message={controller.error ?? 'Try again when the connection is available.'}
            action={
              <Button type="button" variant="outline" onClick={controller.retry}>
                <RotateCw className="size-4" />
                Try again
              </Button>
            }
          />
        )}
        {controller.status === 'ready' &&
          (children ?? (
            <div
              // The card floats outside the full-width scroller. Both message content
              // and composer inherit the same space for it and the navigation rail.
              className={cn(
                'relative flex min-h-0 flex-1 flex-col has-[[data-conversation-navigation-rail]]:@[32rem]/conversation-shell:[--conversation-leading-gutter:2.5rem]',
                floatContext && '[--conversation-context-space:24.5rem]'
              )}
            >
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <ConversationTranscript
                  state={controller.state}
                  projectName={projectName}
                  currentUserId={currentUserId ?? 0}
                  onLoadOlder={controller.loadOlder}
                  onOpenCanvas={openCanvas}
                  onOpenInspector={openInspector}
                  {...(panelServices === undefined ? {} : { loadSourceSummaries: panelServices.getSourceSummaries })}
                  {...(readOnly ? {} : { onAnswer: controller.submit })}
                  {
                    // All three commands address a saved chat's question run or execution, so a
                    // draft has nothing to point them at, and no run to recover from either: the
                    // first message is what creates both the chat and its first task.
                    ...(readOnly || projectId === undefined || conversationId === undefined
                      ? {}
                      : {
                          onAbandonQuestion: (briefId: number, runId: number) =>
                            questionServices
                              .abandonQuestion(projectId, conversationId, briefId, runId)
                              .then(() => undefined),
                          onContinueQuestions: (briefId: number, runId: number) =>
                            questionServices
                              .continueQuestions(projectId, conversationId, briefId, runId)
                              .then(() => undefined),
                          onRetryExecution: (executionId: number) =>
                            questionServices
                              .retryExecution(projectId, conversationId, executionId)
                              .then(() => undefined)
                        })
                  }
                  {...(controller.olderError === undefined ? {} : { olderError: controller.olderError })}
                />
                {!readOnly && (
                  <div className="shrink-0 pr-[var(--conversation-context-space,0px)] pl-[var(--conversation-leading-gutter,0px)]">
                    <ConversationComposer
                      projectName={projectName}
                      globalContentEnabled={globalContentEnabled ?? false}
                      attachmentsEnabled={attachmentsEnabled ?? false}
                      onSubmit={controller.submit}
                      contextActions={composerContext}
                      onDocumentsChange={setPendingDocuments}
                      loadTags={loadTags}
                      loadDocuments={loadDocuments}
                      {...(createTag === undefined ? {} : { createTag })}
                      {...(uploadDocument === undefined ? {} : { uploadDocument })}
                      {...(suggestions === undefined ? {} : { suggestions })}
                      {...(composerSeed === undefined ? {} : { seed: composerSeed })}
                      {...(pendingQuestionKey === undefined ? {} : { pendingQuestionKey })}
                      showOpenings={
                        !controller.state.hasOlder &&
                        controller.state.olderMessages.length === 0 &&
                        controller.state.liveMessages.length === 0 &&
                        Object.keys(controller.state.attempts).length === 0 &&
                        controller.state.changes.length === 0 &&
                        controller.state.tasks.length === 0 &&
                        controller.state.briefs.length === 0 &&
                        controller.state.questions.length === 0 &&
                        controller.state.outputs.length === 0
                      }
                    />
                  </div>
                )}
              </div>
              {floatContext && (
                <aside
                  aria-label="Chat context"
                  data-testid="floating-chat-context"
                  className="absolute top-5 right-6 z-20 flex max-h-[calc(100%-2.5rem)] w-90 flex-col overflow-hidden rounded-2xl border border-border/60 bg-muted/30 shadow-sm"
                >
                  <div className="flex items-center justify-between px-4 pt-3">
                    <h2 className="text-sm font-medium">Chat context</h2>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Collapse chat context"
                      onClick={() => {
                        setContextCollapsed(true)
                        contextTrigger.current?.focus()
                      }}
                    >
                      <X className="size-4" aria-hidden="true" />
                    </Button>
                  </div>
                  <div className="min-h-0 overflow-y-auto overscroll-contain">{contextPanel}</div>
                </aside>
              )}
            </div>
          ))}
      </section>
      <Sheet open={contextSheetOpen && !!contextAvailable} onOpenChange={setContextSheetOpen}>
        <SheetContent
          side="right"
          closeLabel="Close chat context"
          className="w-[min(92vw,22.5rem)] gap-0 overflow-y-auto p-0 sm:max-w-[22.5rem]"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            const action = contextAction.current
            contextAction.current = undefined
            // The clicked sheet row has unmounted. Establish a durable invoker
            // before another inspector records where it should return focus.
            contextTrigger.current?.focus()
            if (action !== undefined) queueMicrotask(action)
          }}
        >
          <SheetHeader className="pr-12 pb-0">
            <SheetTitle>Chat context</SheetTitle>
            <SheetDescription className="sr-only">
              Sources for the answer and material attached to this chat.
            </SheetDescription>
          </SheetHeader>
          {contextPanel}
        </SheetContent>
      </Sheet>
      <ChatInspector
        key={`${projectId ?? controller.state.projectId ?? 'unscoped'}:${conversationId ?? controller.state.threadId ?? 'unscoped'}`}
        projectId={projectId ?? controller.state.projectId ?? 0}
        {...(chatTitle === undefined ? {} : { chatTitle })}
        state={controller.state}
        {...(inspectorIdentity === undefined ? {} : { inspector: inspectorIdentity })}
        onClose={() => {
          if (inspectorIdentity?.inspector === 'sources') setSelectedSourceMessage(inspectorIdentity.messageId)
          onCloseInspector()
        }}
        onNavigate={openInspector}
        restoreFocus={() => {
          if (panelInvoker.current?.isConnected) panelInvoker.current.focus()
          else contextTrigger.current?.focus()
        }}
        onAnswerSlot={seedComposer}
        {...(panelServices === undefined ? {} : { services: panelServices })}
      />
    </div>
  )
}
